import { useAppointment } from '@/src/hooks'
import moment from 'moment'
import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { AppointmentTimeSlot, BookAppointmentForm } from './BookAppointmentTypes'

type TimeslotItem = AppointmentTimeSlot

const formatSlotLabel = (slot: TimeslotItem) => {
  const start = moment(slot.startTime)
  const end = moment(slot.endTime)
  if (!start.isValid() || !end.isValid()) {
    return `${slot.startTime} – ${slot.endTime}`
  }
  return `${start.format('h:mm A')} – ${end.format('h:mm A')}`
}

const slotKey = (slot: TimeslotItem) => `${slot.startTime}\u0000${slot.endTime}`

const slotsMatch = (a: AppointmentTimeSlot | null, b: TimeslotItem) =>
  !!a && a.startTime === b.startTime && a.endTime === b.endTime

export const BookAppointmentStepDateTime = () => {
  const { setValue, watch } = useFormContext<BookAppointmentForm>()
  const date = watch('date')
  const doctor = watch('doctor')
  const facility = watch('facility')
  const selectedTime = watch('time')

  const { timeslots, timeslotIsLoading } = useAppointment({
    date: date ? moment(date).format('YYYY-MM-DD') : undefined,
    facility_id: facility || undefined,
    staff_id: doctor || undefined
  })

  const slotList = useMemo((): TimeslotItem[] => {
    const raw = timeslots?.timeslots
    return Array.isArray(raw) ? raw : []
  }, [timeslots])

  useEffect(() => {
    if (timeslotIsLoading || slotList.length === 0) return
    if (!selectedTime) return
    const stillValid = slotList.some(slot => slotsMatch(selectedTime, slot))
    if (!stillValid) {
      setValue('time', null, { shouldDirty: true, shouldValidate: true })
    }
  }, [timeslotIsLoading, slotList, selectedTime, setValue])

  const hasSelectionCriteria = Boolean(date && doctor)

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="mb-2 text-sm font-medium text-[#2E2E2E]">Available time slot</p>
        {!hasSelectionCriteria ? (
          <p className="text-sm text-[#6B7280]">
            Select a date and doctor on the previous step to load available times.
          </p>
        ) : timeslotIsLoading ? (
          <div aria-label="Loading time slots" className="grid grid-cols-2 md:grid-cols-4 gap-2" role="status">
            {Array.from({ length: 8 }).map((_, i) => (
              <div className="h-10 animate-pulse rounded-md bg-[#F3F3F3]" key={i} />
            ))}
          </div>
        ) : slotList.length === 0 ? (
          <p className="text-sm text-[#6B7280]">No time slots are available for this date. Try another day.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {slotList.map(slot => {
              const value: AppointmentTimeSlot = { endTime: slot.endTime, startTime: slot.startTime }
              const isActive = slotsMatch(selectedTime, slot)
              return (
                <button
                  className={`h-10 rounded-md border px-1 text-xs transition-colors ${
                    isActive
                      ? 'border-[#A79A46] bg-[#A79A46] text-white'
                      : 'border-[#ECECEC] bg-white text-[#2E2E2E] hover:border-[#DDD8B0]'
                  }`}
                  key={slotKey(slot)}
                  onClick={() => setValue('time', value, { shouldDirty: true, shouldValidate: true })}
                  type="button"
                >
                  {formatSlotLabel(slot)}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
